import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router'
import type { BookedAppointment } from '@fitting-room/contracts'
import { fetchBooked } from '../lib/api'
import { BOUTIQUE_LOCALE, BOUTIQUE_TIME_ZONE } from '../lib/boutique'
import { uiStrings, type UiStrings } from '../lib/strings'
import { track } from '../lib/telemetry'
import { Page } from './ReserveRoute'

/** How long to wait for Koalendar's webhook before saying the link is coming by email. */
const PATIENCE_MS = 30_000

/**
 * `/booked` — where Koalendar sends the browser after a booking. `docs/23`.
 *
 * The address carries nothing (Koalendar's "pass event details" stays off, so
 * no name or email lands in a URL or a log); which booking is this browser's
 * is the cookie `/go` or `/reserve` left on the way out. A `booking_id` in
 * the address is still read, for a page that does pass details.
 *
 * Only a guide: the booking is the webhook's, and this asks about once a
 * second until it has landed. Found, it says so and offers the fitting room —
 * a button, not a redirect, so "you're booked" is read before anything else
 * happens. A pickup appointment is found the same way and says only that it
 * is booked: there is no fitting room for a gown already bought. Not found — some other way here, or the webhook slower than half
 * a minute — it is booked all the same, and the link is in the email.
 */
export function BookedRoute() {
  const t = uiStrings(navigator.language || 'en')
  const [params] = useSearchParams()
  const bookingId = params.get('booking_id') ?? params.get('bookingId') ?? undefined
  const [startedAt] = useState(() => Date.now())
  const [patient, setPatient] = useState(true)
  useEffect(() => {
    const timer = setTimeout(() => setPatient(false), PATIENCE_MS)
    return () => clearTimeout(timer)
  }, [])

  const booked = useQuery({
    queryKey: ['public', 'booked', bookingId ?? 'cookie'] as const,
    queryFn: ({ signal }) => fetchBooked(bookingId, signal),
    retry: 2,
    refetchInterval: (query) => (query.state.data?.status === 'waiting' && Date.now() - startedAt < PATIENCE_MS ? 1000 : false),
  })
  const data = booked.data
  const looking = booked.isPending || (data?.status === 'waiting' && patient)
  // Once per visit: found, or how it wasn't (another browser, too late, the webhook slow).
  const outcome = data?.status === 'ready' || data?.status === 'confirmed' ? 'found' : looking ? null : (data?.status ?? 'error')
  useEffect(() => {
    if (outcome) track('booked_return', { outcome })
  }, [outcome])

  if (data?.status === 'ready') {
    return <Ready t={t} path={data.path} appointment={data.appointment} linkByEmail={data.linkByEmail} />
  }
  if (data?.status === 'confirmed') return <Confirmed t={t} appointment={data.appointment} />
  const linkByEmail = data?.linkByEmail ?? true
  return (
    <Page>
      <h1 className="fr-display text-[2rem] leading-[1.08]">{t.bookedTitle}</h1>
      <p className="mt-3 text-[0.9375rem] leading-relaxed" style={{ color: 'var(--fr-ink-dim)' }} role="status">
        {looking ? t.bookedWaiting : linkByEmail ? t.bookedEmailed : t.bookedNoEmail}
      </p>
    </Page>
  )
}

/** A pickup: booked, said back, and nothing more to do. */
function Confirmed({ t, appointment }: { readonly t: UiStrings; readonly appointment: BookedAppointment }) {
  return (
    <Page>
      <p className="fr-eyebrow">{appointment.boutiqueName}</p>
      <h1 className="fr-display mt-2 text-[2rem] leading-[1.08]">
        {appointment.firstName ? t.bookedTitleFor(appointment.firstName) : t.bookedTitle}
      </h1>
      <p className="mt-3 text-[1rem]" data-testid="booked-when">
        {t.bookedWhen(appointment.tierLabel, startsAt(appointment.startsAt))}
      </p>
      <p className="mt-4 text-[0.9375rem] leading-relaxed" style={{ color: 'var(--fr-ink-dim)' }} data-testid="booked-pickup">
        {t.bookedPickupBody}
      </p>
    </Page>
  )
}

function startsAt(iso: string): string {
  return new Intl.DateTimeFormat(BOUTIQUE_LOCALE, {
    timeZone: BOUTIQUE_TIME_ZONE,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso))
}

function Ready({
  t,
  path,
  appointment,
  linkByEmail,
}: {
  readonly t: UiStrings
  readonly path: string
  readonly appointment: BookedAppointment
  readonly linkByEmail: boolean
}) {
  const when = startsAt(appointment.startsAt)
  return (
    <Page>
      <p className="fr-eyebrow">{appointment.boutiqueName}</p>
      <h1 className="fr-display mt-2 text-[2rem] leading-[1.08]">
        {appointment.firstName ? t.bookedTitleFor(appointment.firstName) : t.bookedTitle}
      </h1>
      <p className="mt-3 text-[1rem]" data-testid="booked-when">
        {t.bookedWhen(appointment.tierLabel, when)}
      </p>
      <p className="mt-4 text-[0.9375rem] leading-relaxed" style={{ color: 'var(--fr-ink-dim)' }}>
        {t.bookedReadyBody}
      </p>
      <a className="fr-btn fr-btn-primary mt-7 block w-full text-center" href={path} data-testid="booked-open">
        {t.bookedChooseGowns}
      </a>
      <p className="mt-3 text-[0.8125rem] leading-relaxed" style={{ color: 'var(--fr-ink-faint)' }}>
        {linkByEmail ? t.bookedComeBackEmailed : t.bookedComeBack}
      </p>
    </Page>
  )
}
